/**
 * Bounded, local-only diagnostic trace for the wake/hang investigation
 * (2026-09-23: push-tap and long-hidden-tab loads stuck on "loading" while
 * the Firestore SDK reports nothing). See the root-cause audit in that
 * session: the trace exists to discriminate "transport dead" from "auth
 * token refresh stalled" with one real incident.
 *
 * Safety contract (agreed before build):
 * - Hard caps: MAX_EVENTS entries, MAX_FIELD chars per value, oldest
 *   overwritten first. The buffer can never grow unbounded.
 * - Local only. Nothing here is transmitted anywhere; the only exit is the
 *   admin-gated "Copy diagnostics" control.
 * - Never log tokens, PII, message text, or raw error objects. Callers pass
 *   short keys and flat primitive details (codes, outcomes, durations);
 *   anything else is dropped or truncated here as a second line of defense.
 * - Survives storage being unavailable, full, or blocked: every localStorage
 *   touch is wrapped, and the buffer falls back to memory-only.
 * - A session id + trace-start timestamp head the dump so an old persisted
 *   trace can't be mistaken for the incident being reported.
 * - No auto-clear: an incident usually ends with a reload or a successful
 *   retry, and auto-clearing on success would destroy the evidence of the
 *   incident just survived. The copy UI offers an explicit Clear instead.
 */

const MAX_EVENTS = 200;
const MAX_FIELD = 200;
const STORAGE_KEY = 'pxcTrace_v1';

const sessionId = Math.random().toString(16).slice(2, 10);
const traceStart = Date.now();
let events = [];

// Capture the previous session's persisted trace BEFORE this session's first
// persist overwrites the storage slot. An incident usually ends in a reload;
// the evidence lives in the session that just died.
let priorSession = null;
try {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    const saved = JSON.parse(raw);
    if (saved && Array.isArray(saved.events)) priorSession = saved;
  }
} catch (e) {
  /* unreadable or blocked storage: no prior trace */
}

function sanitizeValue(v) {
  if (typeof v === 'number' || typeof v === 'boolean') return v;
  if (typeof v === 'string') return v.length > MAX_FIELD ? v.slice(0, MAX_FIELD) + '…' : v;
  return undefined; // objects, errors, tokens: never logged
}

function sanitizeDetail(detail) {
  if (!detail || typeof detail !== 'object') return undefined;
  const out = {};
  for (const key of Object.keys(detail).slice(0, 10)) {
    const v = sanitizeValue(detail[key]);
    if (v !== undefined) out[String(key).slice(0, 40)] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

function persist() {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ sid: sessionId, start: traceStart, events })
    );
  } catch (e) {
    /* storage unavailable/full/blocked: memory-only is fine */
  }
}

export function trace(key, detail) {
  try {
    const entry = { t: Date.now(), k: String(key).slice(0, 60) };
    const d = sanitizeDetail(detail);
    if (d) entry.d = d;
    events.push(entry);
    if (events.length > MAX_EVENTS) events = events.slice(events.length - MAX_EVENTS);
    persist();
  } catch (e) {
    /* diagnostics must never break the app */
  }
}

/** Human-readable dump: this session's buffer plus, if present, the previous
 *  session's persisted trace (an incident often ends in a reload, which
 *  starts a new session; the evidence is in the prior one). */
export function traceDump() {
  try {
    let prior = '';
    if (priorSession) {
      prior =
        `--- prior session ${priorSession.sid} (started ${new Date(priorSession.start).toISOString()}) ---\n` +
        priorSession.events.map(formatEntry).join('\n') + '\n';
    }
    return (
      `PXC trace v1 | session ${sessionId} | started ${new Date(traceStart).toISOString()}\n` +
      prior +
      `--- this session ---\n` +
      events.map(formatEntry).join('\n')
    );
  } catch (e) {
    return 'trace unavailable';
  }
}

function formatEntry(e) {
  const detail = e.d ? ' ' + JSON.stringify(e.d) : '';
  return `${new Date(e.t).toISOString()} ${e.k}${detail}`;
}

export function traceClear() {
  events = [];
  priorSession = null;
  try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* fine */ }
}

trace('trace:start', { sid: sessionId });
